import * as THREE from "three";
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { SceneUtils } from 'three/examples/jsm/utils/SceneUtils.js';

import pathToModel from '../../assets/models/ballerina.glb';
import { coloursToArray } from '../../utils';

export default class SceneController {
    constructor(domElem) {
        this.domElem = domElem;
        this.scene = new THREE.Scene();
        this.renderer = new THREE.WebGLRenderer({ antialias: true });
        this.clock = new THREE.Clock();
    }

    setup() {
        this.colourArr = (0, coloursToArray)('https://coolors.co/0a191f-dad2d8-0f8b8d-ec9a29-a8201a');
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(window.devicePixelRatio);
        this.domElem.appendChild(this.renderer.domElement);

        this.disposed = false;
        this.scene.background = this.colourArr[0];

        this.setupCamera();
        this.setupLight();
        this.setupGlobalUniforms();
        this.setupGeometry();
    }

    setupCamera() {
        let VIEW_ANGLE = 70;
        let ASPECT_RATIO = window.innerWidth / window.innerHeight;
        let NEAR_PLANE = 1;
        let FAR_PLANE = 500;

        this.camera = new THREE.PerspectiveCamera(VIEW_ANGLE, ASPECT_RATIO, NEAR_PLANE, FAR_PLANE);
        this.camera.position.set(-50, 10, 60);
        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
    }

    setupLight() {
        let pointLight = new THREE.PointLight(this.colourArr[4], 0.8);
        pointLight.position.y = -50;
        this.scene.add(pointLight);

        let pointLight2 = new THREE.PointLight(this.colourArr[4], 0.8);
        pointLight2.position.y = -10;
        pointLight2.position.x = -100;
        this.scene.add(pointLight2);

        let pointLight3 = new THREE.PointLight(this.colourArr[4], 0.8);
        pointLight3.position.y = -10;
        pointLight3.position.x = 100;
        this.scene.add(pointLight3);

        let ambient = new THREE.AmbientLight(this.colourArr[1]);
        this.scene.add(ambient);

        let dirLight = new THREE.DirectionalLight(this.colourArr[3], 1);
        dirLight.position.set(-1, 0.85, 0, 5).normalize();
        this.scene.add(dirLight);
    }

    setupGlobalUniforms() {
        this.uniforms = {
            u_resolution: { value: new THREE.Vector2(window.innerWidth, window.innerHeight) },
            u_time: { value: 0 },
            u_move: { value: 1.0 }
        };
    }

    setupShaderMaterial() {
        let material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: `
                varying vec3 vNormal;
                varying vec2 vUv; 
            
                void main()
                {
                    vNormal = normal;
                    vUv = uv;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0 );
                }
            `,
            fragmentShader: `
                precision mediump float;

                uniform float u_time;
                uniform vec2 u_resolution;

                varying vec2 vUv;
                varying vec3 vNormal;

                const float fluid_speed = 200.0;
                const float color_intensity = 0.5;

                float rand(vec2 co)
                {
                    return fract(sin(dot(co.xy ,vec2(12.9898,78.233))) * 43758.5453);
                }

                void main()
                {
                    vec2 pos = vUv * (gl_FragCoord.xy/u_resolution);

                    for(int i=1; i<45; i++)
                    {
                        vec2 newPos = pos + u_time * 0.02;
                        newPos.x+=0.6/float(i)*sin(float(i)*pos.y+u_time/fluid_speed+0.3*float(i)) + 0.5;
                        newPos.y+=0.6/float(i)*sin(float(i)*pos.x+u_time/fluid_speed+0.3*float(i+10)) - 0.5;
                        pos = newPos;
                    }

                    vec3 n = vNormal;
                    vec3 col = vec3(color_intensity * sin(0.5 * pos.x) + n.x, 
                                    color_intensity * sin(pos.x + pos.y),
                                    color_intensity * sin(1.0 * pos.y) + n.z);
                    gl_FragColor=vec4(col, 1.0);
                    //gl_FragColor=vec4(col * vNormal, 1.0);
                }
            `
        });
        return material;
    }

    loadModels() {
        let material = this.setupShaderMaterial();
        const gltfLoader = new GLTFLoader();
        let sc = 0.5;
        gltfLoader.load(pathToModel, (gltf) => {
            this.root = gltf.scene;
            this.root.traverse(node => {
                if (node.isMesh) {
                    node.material = material;
                    node.position.z = -20;
                    node.position.y = -30;
                    node.rotation.x = -Math.PI * 0.5;
                    node.rotation.y = Math.PI * 0.5;
                    node.scale.x = sc;
                    node.scale.y = sc;
                    node.scale.z = sc;
                }
            });
            this.scene.add(this.root);
        });
    }

    setupParticleMaterial() {
        let material = new THREE.ShaderMaterial({
            uniforms: this.uniforms,
            vertexShader: `
                uniform float u_time;
                uniform float u_move;
          
                attribute vec3 offset;
                attribute vec4 color;
                attribute vec4 orientation;
                attribute float time;
          
                varying float vRotation;
                varying vec4 vColor;

                vec3 rotateVectorByQuaternion(vec3 v, vec4 q) 
                {
                    vec3 dest = vec3( 0.0 );
                    float x = v.x, y  = v.y, z  = v.z;
                    float qx = q.x, qy = q.y, qz = q.z, qw = q.w;

                    // calculate quaternion * vector
                    float ix =  qw * x + qy * z - qz * y,
                        iy =  qw * y + qz * x - qx * z,
                        iz =  qw * z + qx * y - qy * x,
                        iw = -qx * x - qy * y - qz * z;

                    // calculate result * inverse quaternion
                    dest.x = ix * qw + iw * -qx + iy * -qz - iz * -qy;
                    dest.y = iy * qw + iw * -qy + iz * -qx - ix * -qz;
                    dest.z = iz * qw + iw * -qz + ix * -qy - iy * -qx;

                    return dest;
                }

                vec4 axisAngleToQuaternion( vec3 axis, float angle ) 
                {
                    vec4 dest = vec4( 0.0 );
                    float halfAngle = angle / 2.0,
                        s = sin( halfAngle );

                    dest.x = axis.x * s;
                    dest.y = axis.y * s;
                    dest.z = axis.z * s;
                    dest.w = cos( halfAngle );

                    return dest;
                }
            
                void main()
                {
                    float localTime = time + u_time;
                    float modTime = mod(localTime, 1.0);

                    vec3 off = offset;
                    vec3 vPosition = position * 0.5;
                    vRotation = u_time * 10.0 + localTime * 20.0 + orientation.w * 100.0;
                    vec4 qRotation = axisAngleToQuaternion(orientation.xyz, vRotation);
                    vPosition = rotateVectorByQuaternion(vPosition, qRotation);
        
                    off.y += modTime * -2000.0;
                    off.y -= sin(localTime * 6.0 + (off.x)) * 40.0;
                    off.z += cos(time * 60.0 + (off.z)) * 100.0;
                    off.x += sin(time * 120.0 + (off.y*0.01)) * 100.0;

                    if (u_move == 1.0) 
                    {
                        off.z += modTime*2000.0;
                    }

                    vColor = color;
                    gl_Position = projectionMatrix * modelViewMatrix * vec4(off+vPosition, 1.0);
                }
            `,
            fragmentShader: `
                precision mediump float;

                varying float vRotation;
                varying vec4 vColor;
                
                void main()
                {
                    gl_FragColor = vec4(vColor.xyz*abs(sin(vRotation)), 1.0);
                }
            `
        });
        return material;
    }

    setupGeometry() {
        this.surfaceArray = [];
        let plane = new THREE.PlaneGeometry(1000, 1000, 40, 40);
        for (let i = 0; i < plane.vertices.length; i++) {
            //how much displacement on z
            let value = Math.random() * 40 - 10;
            plane.vertices[i].z = value;
            this.surfaceArray.push(value);
        }
        plane.computeVertexNormals();
        plane.computeFaceNormals();

        let materials = [
            new THREE.MeshPhongMaterial({ color: this.colourArr[2], specular: this.colourArr[3], flatShading: true }),
            new THREE.MeshBasicMaterial({ color: this.colourArr[3], wireframe: true, transparent: true })
        ];

        this.surface = SceneUtils.createMultiMaterialObject(plane, materials);
        this.surface.rotation.x = -Math.PI / 2;
        this.surface.rotation.z = Math.PI;
        this.surface.position.y = -50;
        this.scene.add(this.surface);

        this.addSnowParticles();
        this.plane = plane;
    }

    addSnowParticles() {
        let triangles = 1;
        let instances = 3000;
        let geometry = new THREE.InstancedBufferGeometry();
        geometry.maxInstancedCount = instances;

        let vertices = new THREE.BufferAttribute(new Float32Array(triangles * 3 * 3), 3);
        let size = 6;

        vertices.setXYZ(0, size, -size, 0);
        vertices.setXYZ(1, -size, size, 0);
        vertices.setXYZ(2, 0, 0, size);
        geometry.setAttribute('position', vertices);

        let offsets = new THREE.InstancedBufferAttribute(new Float32Array(instances * 3), 3, false, 1);
        for (let i = 0, ul = offsets.count; i < ul; i++) {
            //offsets.setXYZ( i, Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5 );
            offsets.setXYZ(i, Math.random() * 1000 - 500, 0, Math.random() * 1000 - 500);
        }
        geometry.setAttribute('offset', offsets);

        let colors = new THREE.InstancedBufferAttribute(new Float32Array(instances * 4), 4, false, 1);
        for (let i = 0, ul = colors.count; i < ul; i++) {
            //colors.setXYZW( i, Math.random(), Math.random(), Math.random(), Math.random() );
            colors.setXYZW(i, 1, 1, 1, 1);
        }
        geometry.setAttribute('color', colors);

        let vector = new THREE.Vector4();
        let orientations = new THREE.InstancedBufferAttribute(new Float32Array(instances * 4), 4, false, 1);
        for (let i = 0, ul = orientations.count; i < ul; i++) {
            vector.set(Math.random() * 2 - 1, Math.random() * 2 - 1, Math.random() * 2 - 1, Math.random() * 2 - 1);
            vector.normalize();
            orientations.setXYZW(i, vector.x, vector.y, vector.z, vector.w);
        }
        geometry.setAttribute('orientation', orientations);

        let times = new THREE.InstancedBufferAttribute(new Float32Array(instances * 1), 1, false, 1);

        for (let i = 0, ul = times.count; i < ul; i++) {
            times.setX(i, Math.random());
        }
        geometry.setAttribute('time', times);

        let partMaterial = this.setupParticleMaterial();
        let snowparticles = new THREE.Mesh(geometry, partMaterial);
        snowparticles.position.y = 950;
        snowparticles.position.z = 500;
        snowparticles.rotation.y = Math.PI;
        snowparticles.frustumCulled = false;
        this.scene.add(snowparticles);
    }

    orbitCamera(rotSpeed) {
        this.camera.position.x += Math.sin(rotSpeed * 0.0003);
        this.camera.position.z += Math.cos(rotSpeed * 0.0003);
        this.camera.position.y += Math.cos(rotSpeed * 0.0003);

        this.camera.lookAt(new THREE.Vector3(0, 0, 0));
        this.camera.up.z += Math.sin(rotSpeed * 0.0001);
        this.camera.up.x += Math.cos(rotSpeed * 0.0001);
    }

    updateSceneSubjects(time) {
        for (let i = 0; i < this.plane.vertices.length; i++) {
            this.plane.vertices[i].z = Math.sin((time / 200) + (this.plane.vertices[i].x) / 50 - i) * (this.surfaceArray[i] + Math.cos(time));
        }

        this.plane.verticesNeedUpdate = true;
        this.plane.computeFaceNormals();
        this.plane.normalsNeedUpdate = true;

        this.surface.position.z -= time * 2;
        if (this.surface.position.z < -1300) this.surface.position.z += 1800;
    }

    update() {
        if (this.disposed) return;
        let delta = this.clock.getDelta();

        if (isNaN(delta) || delta > 1000 || delta === 0) {
            delta = 1000 / 60;
        }
        //if(this.camera) this.orbitCamera(0.001);
        if (this.uniforms) this.uniforms['u_time'].value += 0.01 * delta;
        this.updateSceneSubjects(delta);
        this.renderer.render(this.scene, this.camera);
    }

    onWindowResize() {
        this.camera.aspect = window.innerWidth / window.innerHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(window.innerWidth, window.innerHeight);
    }

    dispose() {
        this.disposed = true;
        this.scene.children.forEach(child => {
            if (child.isMesh) {
                child.material.dispose();
                child.geometry.dispose();
            }
        });
        this.scene.dispose();
        this.renderer.dispose();
    }
}
